import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import FramlAlertDetailShell from './FramlAlertDetailShell';
import SmartAssessment from './SmartAssessment';
import { isFramlNewLayout } from './FramlAlertDetailShell/framlLayoutToggle';
import Wait4Me from '../../../components/Wait4Me';
import { resolveDownloadModule } from '../../constants/commonConstants';
import {
  getAMLSCommonAlertDetails,
  resetAMLSCommonAlertDetails
} from '../../../redux/actions/common';

// amlsCommon.alertDetails.data.alertSourceId arrives as the NUMERIC source
// code (e.g. '25' / '29') in the run-instance (RI) context, but as the string
// label ('RTFRAUDIAD' / 'RTFRAUDIAD_BATCH') elsewhere. Accept both so the
// FRAML-P module (and its Triggered Only switch) resolves in either context.
// 24 = RealtimeFraudIadRunInstance, 25 = RealtimeFraudIadProduction (RTFRAUDIAD),
// 29 = RealtimeFraudIadProductionBatch (RTFRAUDIAD_BATCH) — see
// be/.../enums/AlertSource.scala. Run-instance alerts persist with source 24.
const FRAML_SOURCE_IDS = ['RTFRAUDIAD', 'RTFRAUDIAD_BATCH', '24', '25', '29'];

const FramlAlertDetailRouter = ({
  module,
  dispatch,
  framlLayout,
  isFramlP,
  hasResolvedSource,
  sourceLookupFailed,
  isCmSurface,
  ...props
}) => {
  const alertId = props.match?.params?.alertId || props.alertId;

  /* FIN-13723 (second attempt) — THE ROUTER OWNS THE SOURCE LOOKUP.
     The first attempt read alertSourceId out of amlsCommon.alertDetails and
     assumed it would arrive. On the RI route it never does, because the only
     thing that fetches it is AlertTopPanelDetails — a component rendered INSIDE
     the view this router is trying to choose. Measured on keycloak-qa against a
     TM Detection alert: 214 GET_AMLS_COMMON_ALERT_DETAILS dispatches in 15s and
     the slice stuck at {isLoading: true, data: null} forever, so the guard could
     never fire and the Spatial shell stayed mounted.
     It is self-sustaining: the shell renders AlertHistoryPrevention, which
     cannot parse a TM alert's triggeredRulesActionSummary (1371 parse errors in
     one page load), the render storm remounts the panel, its useEffect refires,
     and the reducer resets the slice to isLoading on every GET.
     So the router fetches the source itself and mounts NEITHER view until it
     knows the answer.

     FIN-14085 (CM path) — the fetch now runs on CM too. The original guard
     `!isCmSurface` kept CM out because the TM-alert render-storm cycle above
     needs the router's product-state machine (Wait4Me → classic/spatial) to be
     the one choosing — on CM there was no choosing, the shell mounted
     unconditionally, so there was nothing to protect. But that also left
     `amlsCommon.alertDetails.data` empty on CM, which was fine while the
     EventTimeline legend was hardcoded strings. Once the legend started reading
     `alertDetails.scenarioId` to pick its thresholds (FIN-13569 + the FIN-14085
     fixes), CM lost the scenario override and fell back to DEFAULT_SCORE_BANDS,
     so an alert configured 80 / 89 / 99 rendered its legend as 0–33 / 34–66 /
     67–100. CM's `COMPONENT_BY_SOURCE_ID` only routes FRAML-P sources
     (RTFRAUDIAD / RTFRAUDIAD_BATCH) to this router, so no TM-detection alert
     reaches this fetch via CM — the render-storm precondition can't apply here,
     and the CM FRAML path now needs `amlsCommon.alertDetails.data` for its
     scenarioId. */
  useEffect(() => {
    if (alertId) {
      dispatch(getAMLSCommonAlertDetails({ alertId }));
    }
  }, [alertId]);

  /* LATCHED PER alertId, and this is load-bearing. Once the shell mounts for a
     fraud alert, AlertTopPanelDetails dispatches the same GET, and the reducer
     resets the slice to {isLoading: true, data: null}. Reading the live slice
     would then flip us back to the loader, remount everything and start the
     exact refetch loop this change exists to end. The first resolved answer for
     an alert wins and is held. */
  const [product, setProduct] = useState(null);

  useEffect(() => {
    setProduct(null);
  }, [alertId]);

  useEffect(() => {
    if (product !== null) return;
    if (hasResolvedSource) setProduct(isFramlP ? 'spatial' : 'classic');
    // A failed lookup falls back to Classic, never Spatial: Spatial is the
    // FRAML-P surface, so "we could not tell" must not open it.
    else if (sourceLookupFailed) setProduct('classic');
  }, [hasResolvedSource, isFramlP, sourceLookupFailed, product]);

  useEffect(() => {
    return () => {
      dispatch(resetAMLSCommonAlertDetails());
    };
  }, []);

  /* FIN-11304: THE SHELL IS ALWAYS MOUNTED; the resolved layout chooses which
     view it OPENS ON.
     
     CHANGED 2026-09-17 at the user's direction, reversing the original design —
     read this before "restoring" it. The router used to pick the COMPONENT:
     
         isFramlNewLayout(framlLayout) ? FramlAlertDetailShell : SmartAssessment
     
     which made the two paths asymmetric. The shell renders the Classic/Spatial
     toggle and can already render SmartAssessment inside itself (see its
     `isSpatial` branch), so a tenant on 'new' got a toggle and could move freely;
     a tenant on 'legacy' was handed the bare legacy component with no toggle and
     no way back. The reported symptom was exactly that — Spatial with a toggle
     under ?framlLayout=new, Classic with none once the config line was removed.
     
     WHAT THIS GIVES UP. PRD 3122987032 says a Legacy client must not reach the
     new view "via any URL or navigation"; with the toggle present in both, it is
     now one click away for every FRAML-P user. The user was shown that trade-off
     and asked for the toggle in both scenarios, so `framlAlertDetailLayout` is
     now a DEFAULT — which is what config.js has always called it — rather than a
     gate. Re-gating it means restoring the component switch above, not hiding
     the toggle, or the shell's chrome ships without its control.
     
     WHAT IT DOES NOT GIVE UP: nobody LANDS on the redesign. A tenant without the
     flag still opens on Classic content. */
  /* FIN-13723 — SPATIAL IS NOT FOR TM DETECTION.
     It serves FRAML-P run-instance alerts AND CM alerts; the one product it was
     never specified for is TM Detection, so this excludes that rather than
     restricting Spatial to Fraud Prevention.
     `/app/trm/run-instance/:alertId` is shared by FRAML-P and TM Detection run
     instances, so this router was mounting the Spatial shell for both — a TM
     Detection alert got the Classic/Spatial toggle and could be opened in a view
     built around per-event fraud scores, variant actions and fraud trigger
     evidence, none of which it has.
     RETURNING CLASSIC RATHER THAN HIDING THE TOGGLE: the shell IS the Spatial
     surface, so hiding its control would leave the chrome in place and the view
     still reachable by URL, which the ticket asks to prevent as well.
     CM IS UNAFFECTED: CM AlertDetails passes a resolved `module`, mapStateToProps
     returns early for it, and the shell mounts exactly as before — no lookup, no
     loader, no gate. */
  if (!isCmSurface) {
    /* MOUNT NEITHER VIEW UNTIL THE SOURCE IS KNOWN. This is the part that breaks
       the cycle: rendering the shell "for now" is what generates the render storm
       that stops the source ever resolving. A brief loader is the honest state —
       we genuinely do not know yet which product this alert belongs to. */
    if (product === null) {
      return <Wait4Me />;
    }
    if (product === 'classic') {
      return <SmartAssessment {...props} />;
    }
  }

  return (
    <FramlAlertDetailShell
      initialViewMode={isFramlNewLayout(framlLayout) ? 'spatial' : 'classic'}
      module={module}
      {...props}
    />
  );
};

FramlAlertDetailRouter.propTypes = {
  module: PropTypes.string,
  dispatch: PropTypes.func.isRequired,
  framlLayout: PropTypes.string,
  // FIN-13723 — both undefined until the alert resolves, and in CM context.
  isFramlP: PropTypes.bool,
  hasResolvedSource: PropTypes.bool,
  sourceLookupFailed: PropTypes.bool,
  isCmSurface: PropTypes.bool,
  // FIN-13723 — the RI route supplies the alert through match params; CM passes
  // it directly. The router needs it to fetch the source itself.
  match: PropTypes.shape({
    params: PropTypes.shape({ alertId: PropTypes.string })
  }),
  alertId: PropTypes.string
};

const mapStateToProps = (state, ownProps) => {
  // FIN-11304 BE-2 wiring point: once the backend exposes the tenant
  // `framl_alert_detail_layout` dyn-property in state, read it here and return
  // it as `framlLayout`. Until then it is left undefined and the toggle helper
  // defaults to the legacy view (dev override still works locally).
  // e.g. const framlLayout = state.amlsCommon?.uiConfig?.framl_alert_detail_layout;

  // CM AlertDetails passes a fully-resolved module prop (CM_FRAML_*).
  // Don't override it from Redux — amlsCommon.alertDetails is not populated
  // in CM context, and overriding would downgrade CM_FRAML_* → undefined.
  if (ownProps.module) return { isCmSurface: true };
  const sourceId = state.amlsCommon?.alertDetails?.data?.alertSourceId;
  const isFramlP =
    sourceId != null && FRAML_SOURCE_IDS.includes(String(sourceId));
  const module = sourceId
    ? resolveDownloadModule({ isFromCM: false, isFramlP })
    : undefined;
  // FIN-13723 — the same two facts the module already keys off, surfaced so the
  // component can refuse Spatial for a non-fraud alert, plus the failure case so
  // a lookup that errors falls back to Classic instead of hanging on the loader.
  return {
    module,
    isFramlP,
    hasResolvedSource: sourceId != null,
    sourceLookupFailed: state.amlsCommon?.alertDetails?.error != null,
    isCmSurface: false
  };
};

export default withRouter(connect(mapStateToProps)(FramlAlertDetailRouter));
